import React, { useState, useMemo } from "react";
import { Menu, MenuItem, Button, useTheme } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import { isSameDay } from "date-fns";

const TimeMenu = ({ selectedTime, setSelectedTime, isTimeReserved }) => {
  const [anchorElHours, setAnchorElHours] = useState(null);
  const [anchorElMinutes, setAnchorElMinutes] = useState(null);
  const theme = useTheme();
  const now = new Date();

  const handleHoursClick = (event) => {
    setAnchorElHours(event.currentTarget);
  };

  const handleMinutesClick = (event) => {
    setAnchorElMinutes(event.currentTarget);
  };

  const handleClose = (newHours, newMinutes) => {
    if (selectedTime !== null) {
      const newSelectedTime = new Date(selectedTime.getTime());

      if (newHours !== null) {
        newSelectedTime.setHours(newHours);

        // Get all possible minutes
        const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

        // Find the nearest available minute greater than the current
        const nextAvailableMinute = minutes.find((minute) => {
          const testDate = new Date(newSelectedTime.getTime());
          testDate.setMinutes(minute);
          return !isTimeReserved(testDate,"minute");
        });

        // If an available minute is found, set it, otherwise reset to zero
        if (nextAvailableMinute !== undefined) {
          newSelectedTime.setMinutes(nextAvailableMinute);
        } else {
          newSelectedTime.setMinutes(0);
        }

      } else if (newMinutes !== null) {
        newSelectedTime.setMinutes(newMinutes);
      }

      setSelectedTime(newSelectedTime);
    }

    // Close the menus
    setAnchorElHours(null);
    setAnchorElMinutes(null);
  };

  // Generate available hours
  const filterFutureHours = useMemo(() => {
    let nowHours = 0;
    if (isSameDay(selectedTime, now)) {
      nowHours = now.getHours();
    }
    return Array.from({ length: 24 - nowHours }, (_, i) => {
      const hour = (i + nowHours).toString().padStart(2, "0");
      const hourDate = new Date(selectedTime);
      hourDate.setHours(parseInt(hour, 10), 0, 0, 0);
      hourDate.setMinutes(hourDate.getMinutes() + 30);
      return {
        hour,
        isReserved: isTimeReserved(hourDate,"hour"),
      };
    });
  }, [selectedTime, now, isTimeReserved]);

  // Generate available minutes
  const generateMinutes = () => {
    return Array.from({ length: 12 }, (_, i) =>
      (i * 5).toString().padStart(2, "0")
    );
  };

  // Filter minutes based on selected hour
  const filterFutureMinutes = useMemo(() => {
    if (selectedTime) {
      const currentHour = selectedTime.getHours();
      const nowHour = now.getHours();
      let minutes = generateMinutes();

      if (isSameDay(selectedTime, now) && currentHour === nowHour) {
        minutes = minutes.filter(
          (minute) =>
            parseInt(minute, 10) >= Math.ceil(now.getMinutes() / 5) * 5
        );
      }

      return minutes.map((min) => {
        const minDate = new Date(selectedTime);
        minDate.setMinutes(parseInt(min, 10));

        return {
          minute: min,
          isReserved: isTimeReserved(minDate,"minute"),
        };
      });
    }
    return [];
  }, [selectedTime, now, isTimeReserved]);

  const openHours = Boolean(anchorElHours);
  const openMinutes = Boolean(anchorElMinutes);

  return (
    <div>
      <Button
        id="hours-select"
        aria-expanded={openHours ? "true" : undefined}
        onClick={handleHoursClick}
        endIcon={<KeyboardArrowDown />}
        disabled={selectedTime == null}
        sx={{
          border: 1,
          borderRadius: 1,
color: "white",
          fontWeight: "bold",
          fontSize: "large",
          marginRight: 1,
        }}
      >
        {selectedTime != null
          ? selectedTime.getHours().toString().padStart(2, "0")
          : "小時"}
      </Button>

      <Button
        id="minutes-select"
        aria-expanded={openMinutes ? "true" : undefined}
        onClick={handleMinutesClick}
        endIcon={<KeyboardArrowDown />}
        disabled={selectedTime == null}
        sx={{
          border: 1,
          borderRadius: 1,

color: "white",
          fontWeight: "bold",
          fontSize: "large",
        }}
      >
        {selectedTime != null
          ? selectedTime.getMinutes().toString().padStart(2, "0")
          : "分鐘"}
      </Button>

      <Menu
        id="hours-menu"
        anchorEl={anchorElHours}
        keepMounted
        open={openHours}
        onClose={() => handleClose(null, null)}
      >
        {filterFutureHours.map(({ hour, isReserved }, index) => (
          <MenuItem
            key={index}
            disabled={isReserved}
            onClick={() => handleClose(parseInt(hour, 10), null)}
          >
            {hour} {isReserved ? "(已預定)" : ""}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        id="minutes-menu"
        anchorEl={anchorElMinutes}
        keepMounted
        open={openMinutes}
        onClose={() => handleClose(null, null)}
      >
        {filterFutureMinutes.map(({ minute, isReserved }, index) => (
          <MenuItem
            key={index}
            disabled={isReserved}
            onClick={() => handleClose(null, parseInt(minute, 10))}
          >
            {minute} {isReserved ? "(已預定)" : ""}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default TimeMenu;
